// What PostHog may carry out of the browser.
//
// History. #2511 (2026-09-29) found that `mask_all_text` /
// `mask_all_element_attributes` sat INSIDE `autocapture: {}`, where posthog-js
// never reads them, so clicked-element text shipped for three months; it then
// switched every content-capable feature off. #4475 (founder, 2026-10-16:
// "Turn on everything.. we need to grow our business first.. turn it on and
// start analyzing every session") turned session replay, heatmaps, dead clicks
// and web vitals back ON for every visitor. Replay now records the page as the
// visitor saw it: layout, clicks, scrolling and the visible page text and
// attributes (the founder's call). The public pages say so (Privacy Policy,
// Student Data Principles, landing, docs/analytics.md); change them in the
// same PR as anything here.
//
// What still never leaves, and where each rule lives:
//
//   1. Typed text. `maskAllInputs` masks every input, textarea and select in
//      replay, and the autocapture keys at the TOP LEVEL keep element text and
//      attribute values out of click events (a click event's text would carry
//      what a replay block hides).
//   2. Conversations with Astra. A student's question rendered in a chat
//      bubble is no longer an input, so the bubbles (both sides) carry
//      REPLAY_PRIVATE_CLASS and replay records an empty box in their place:
//      no text, no attributes. Masking text alone is not enough there: a
//      reply's link attributes (a 988 href) would still be recorded.
//   3. Crisis (#4294). Crisis panels carry `ph-no-capture` (blocked in replay
//      AND in autocapture); before_send drops any event naming a crisis or
//      touching a crisis element.
//   4. Admin and Desk pages (other students' emails, Astra traces) are wrapped
//      in `ph-no-capture` by AdminGate.
//   5. Request and response bodies and headers: recordBody/recordHeaders false
//      (the client false beats the project setting in the network plugin), and
//      maskReplayNetworkRequest strips them anyway and cuts every recorded URL
//      to origin + path (+ campaign params).
//   6. Emails as identities, query strings, fragments, page titles and copied
//      text: scrubCaptureEvent as `before_send`, fail closed. That includes
//      every URL attribute inside replay snapshots (rrweb records an in-page
//      "#x" link as the page's full URL, query and all).
//   7. Console output, exceptions, surveys, site apps and the logs product:
//      switched off client-side, and allowExternalScript refuses to load any
//      PostHog script that is not the recorder, web vitals or dead clicks. The logs product has NO client-side veto once its script
//      loads (posthog-logs.js onRemoteConfig turns it on from the project
//      settings alone); refusing its script is the only switch.
//
// The /flags request is still never made (advanced_disable_feature_flags):
// its body carries the RAW first-visit URL ($initial_current_url, query and
// fragment) outside before_send, and the app uses no PostHog flags. Remote
// config (/array/<token>/config.js) does load now: replay starts only when the
// project says recording is on.
//
// Cost lever: `session_recording.sampleRate` set here beats the project's
// setting. Unset means every session is recorded.
//
// Internal accounts are excluded at ANALYSIS time (analytics_identity
// team_traffic_sql); the emit path is not forked for them.
//
// Pinned by __tests__/analyticsPrivacyConfig.test.js (shape + scrub) and
// tests-e2e/analytics-privacy.spec.js (a real browser, real posthog-js,
// every request body decoded and searched for synthetic markers).

// Query parameters a reported URL may keep: campaign attribution only, which
// is ours (we write it into ad and email links), never the student's. Every
// other parameter goes — the app's own include typed search text (q), a
// prefilled Astra question (ask), an email (/feedback?e=) and redeemable
// secrets (grant, token). An allowlist, so a parameter added next month is
// dropped until someone decides otherwise.
export const REPORTABLE_URL_PARAMS = Object.freeze([
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
    'gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid',
]);

/** A URL as analytics may report it: no fragment (the /review magic link
 * carries its bearer secret there), and only REPORTABLE_URL_PARAMS in the
 * query. A relative path stays relative. A value that is not a URL at all
 * (PostHog's '$direct' referrer) passes through, cut at any '?' or '#'
 * (review 2026-09-29: 'page?e=...', '?q=...' and '#frag' came back whole). */
export function safeUrl(value) {
    if (typeof value !== 'string' || value === '') return value;
    const hasScheme = /^[a-z][a-z0-9+.-]*:/i.test(value);
    const protocolRelative = value.startsWith('//');
    const rooted = value.startsWith('/') && !protocolRelative;
    if (!hasScheme && !protocolRelative && !rooted) return value.split(/[?#]/)[0];
    let url;
    try {
        url = new URL(value, 'https://relative.invalid');
    } catch {
        return '';
    }
    // mailto:/tel:/javascript: carry the address or the code in the "path".
    if (!/^https?:$/.test(url.protocol)) return `${url.protocol}`;
    const kept = new URLSearchParams();
    for (const [k, v] of url.searchParams) {
        if (REPORTABLE_URL_PARAMS.includes(k)) kept.append(k, v);
    }
    const qs = kept.toString();
    const path = url.pathname + (qs ? `?${qs}` : '');
    if (rooted) return path;
    if (protocolRelative) return `//${url.host}${path}`;
    return `${url.origin}${path}`;
}

// Event types whose whole purpose is to carry content: copied text, an error
// message and stack, console output, a survey answer. Each is also switched
// off in the config; this list is the backstop. Replay ($snapshot) and
// $web_vitals left this list in #4475: they are wanted now, and their
// content rules live in the recorder config and scrubWebVital.
const CONTENT_EVENTS = new Set([
    '$copy_autocapture', '$exception', 'survey sent', '$log', '$console_log',
]);

// Properties that ARE content. `title` is document.title, which on a school
// or essay page names what the student is reading.
const CONTENT_PROPS = [
    '$el_text', '$selected_content', '$copy_type', 'title',
    // A search-engine query parsed from the referrer.
    'ph_keyword', '$initial_ph_keyword', '$session_entry_ph_keyword',
    '$exception_list', '$exception_message', '$exception_type',
    '$exception_values', '$exception_stack_trace_raw',
];

// Any property whose name says it holds a URL/path is cut by safeUrl.
const URLISH_KEY = /(url|referrer|href)$/i;

function scrubElement(el) {
    if (!el || typeof el !== 'object') return el;
    const out = {};
    for (const [k, v] of Object.entries(el)) {
        if (k === 'tag_name' || k === 'nth_child' || k === 'nth_of_type'
            || k === 'classes' || k === 'attr_class' || k === 'order') {
            out[k] = v;
        } else if (k === 'attr__href' || k === 'href') {
            out[k] = safeUrl(v);
        }
        // everything else ($el_text, text, attr_id, attr__*) is dropped
    }
    return out;
}

// $elements_chain: `tag.class:key="value"key="value";tag...` with `"` inside a
// value escaped as \". Keep structural keys, cut hrefs, drop the rest.
const CHAIN_PAIR = /([A-Za-z0-9_$-]+)="((?:\\"|[^"])*)"/g;
function scrubChain(chain) {
    if (typeof chain !== 'string') return chain;
    return chain.replace(CHAIN_PAIR, (whole, key, value) => {
        if (key === 'nth-child' || key === 'nth-of-type') return whole;
        if (key === 'href' || key === 'attr__href') {
            const cut = safeUrl(value.replace(/\\"/g, '"')).replace(/"/g, '\\"');
            return `${key}="${cut}"`;
        }
        return '';
    });
}

// A web-vitals metric as reported: the numbers and a cut URL. `entries`
// (PerformanceEntry objects naming the element and resource URLs) and
// `attribution` (selectors, URLs) go.
const WEB_VITAL_FIELDS = ['name', 'value', 'delta', 'rating', 'id', 'navigationType',
    '$current_url', '$session_id', '$window_id', 'timestamp'];
const WEB_VITAL_EVENT = /^\$web_vitals_[A-Za-z]+_event$/;
function scrubWebVital(metric) {
    if (!metric || typeof metric !== 'object') return undefined;
    const out = {};
    for (const k of WEB_VITAL_FIELDS) if (k in metric) out[k] = metric[k];
    if (typeof out.$current_url === 'string') out.$current_url = safeUrl(out.$current_url);
    return out;
}

// Replay snapshots (rrweb events: full snapshots, mutations, the page-URL
// meta event). rrweb records href/src as ABSOLUTE URLs, so an in-page
// "#section" link carries the page's whole query string: ?ask=<a prefilled
// Astra question>, /feedback?e=<email>, a grant (found by the e2e marker
// test, #4475). Every URL-valued attribute is cut by safeUrl; srcset per
// candidate. Requires compress_events: false, or the data arrives gzipped
// and this walk sees nothing (pinned in the config test). Returns a copy:
// rrweb's own objects are never mutated.
const REPLAY_URL_KEYS = new Set(['href', 'src', 'action', 'formaction', 'poster', 'xlink:href', 'background', 'cite']);
function scrubReplay(value, key) {
    if (typeof value === 'string') {
        if (REPLAY_URL_KEYS.has(key)) return safeUrl(value);
        if (key === 'srcset') {
            return value.split(',').map((part) => {
                const [u, ...rest] = part.trim().split(/\s+/);
                return [safeUrl(u), ...rest].join(' ');
            }).join(', ');
        }
        return value;
    }
    if (Array.isArray(value)) return value.map((v) => scrubReplay(v));
    if (value && typeof value === 'object') {
        const out = {};
        for (const [k, v] of Object.entries(value)) out[k] = scrubReplay(v, k);
        return out;
    }
    return value;
}

function scrubProps(props) {
    if (!props || typeof props !== 'object') return props;
    const out = { ...props };
    for (const k of CONTENT_PROPS) delete out[k];
    for (const k of ['$snapshot_data', '$snapshot_items']) {
        if (k in out) out[k] = scrubReplay(out[k]);
    }
    for (const k of Object.keys(out)) {
        if (WEB_VITAL_EVENT.test(k)) out[k] = scrubWebVital(out[k]);
    }
    for (const [k, v] of Object.entries(out)) {
        if (typeof v === 'string' && URLISH_KEY.test(k)) out[k] = safeUrl(v);
    }
    if (Array.isArray(out.$elements)) out.$elements = out.$elements.map(scrubElement);
    if ('$elements_chain' in out) out.$elements_chain = scrubChain(out.$elements_chain);
    if (out.$heatmap_data && typeof out.$heatmap_data === 'object') {
        const byUrl = {};
        for (const [u, points] of Object.entries(out.$heatmap_data)) {
            const key = safeUrl(u);
            byUrl[key] = (byUrl[key] || []).concat(points);
        }
        out.$heatmap_data = byUrl;
    }
    // Data-attribute augments (data-ph-capture-attribute-*) are an explicit
    // developer opt-in the app does not use; nothing else lands at top level.
    return out;
}

// Privacy incident #4294: a crisis (988) or distress signal about a student
// (often a minor) is counted only in our own content-free server counters,
// never sent to PostHog or GA4. One word list for both chokepoints here and
// server_analytics.WELLBEING_TERMS on the server (pinned equal by
// test_crisis_never_to_analytics.py).
export const WELLBEING_TERMS = Object.freeze(['crisis', 'distress']);

const _wellbeing = (v) => typeof v === 'string' && WELLBEING_TERMS.some((t) => v.toLowerCase().includes(t));

/** True when an event names a crisis or distress signal: in its name, a
 * property key, or a string value (or a list of them). Fail closed: a school
 * slug that happens to contain the word loses its event, never the reverse. */
export function carriesCrisis(name, props = {}) {
    if (_wellbeing(name)) return true;
    return Object.entries(props || {}).some(([k, v]) => _wellbeing(k) || _wellbeing(v)
        || (Array.isArray(v) && v.some(_wellbeing)));
}

// The app's own properties: everything PostHog did not add ($-prefixed keys
// carry URLs, referrers and library fields a word list must not judge).
const ownProps = (p) => Object.fromEntries(Object.entries(p || {}).filter(([k]) => !k.startsWith('$')));

/** An autocapture that touched a crisis surface: the 988 call link (`tel:`,
 * the only one in the app) or an element named for a crisis. The crisis
 * panels also carry ph-no-capture; this is the backstop (#4294). */
function touchesCrisis(p) {
    const chain = JSON.stringify([p.$elements || null, p.$elements_chain || null]);
    return /tel:|crisis|distress|988/i.test(chain);
}

/** posthog-js `before_send`. Returns the scrubbed event, or null to drop it.
 * Never throws: a scrub that fails drops the event rather than sending it. */
export function scrubCaptureEvent(event) {
    try {
        if (!event || typeof event !== 'object') return null;
        if (CONTENT_EVENTS.has(event.event)) return null;
        // An identity that is an email (a legacy cookie before the init-time
        // migration rebinds it) never leaves: drop the event.
        const p = event.properties || {};
        for (const k of ['distinct_id', '$user_id', '$device_id', '$anon_distinct_id']) {
            if (String(p[k] ?? '').includes('@')) return null;
        }
        // Every event, whatever door it came through (track, autocapture,
        // register's super properties, $set): no crisis signal leaves (#4294).
        if (carriesCrisis(event.event, ownProps(p))) return null;
        for (const s of [event.$set, event.$set_once, p.$set, p.$set_once]) {
            if (s && carriesCrisis('', ownProps(s))) return null;
        }
        // $autocapture, $rageclick and $dead_click all carry the element chain.
        if (('$elements' in p || '$elements_chain' in p) && touchesCrisis(p)) return null;
        const out = { ...event, properties: scrubProps(event.properties) };
        if (event.$set) out.$set = scrubProps(event.$set);
        if (event.$set_once) out.$set_once = scrubProps(event.$set_once);
        if (out.properties?.$set) out.properties.$set = scrubProps(out.properties.$set);
        if (out.properties?.$set_once) out.properties.$set_once = scrubProps(out.properties.$set_once);
        return out;
    } catch {
        return null;
    }
}

// Replay records an empty box in place of any element carrying this class:
// no text, no attributes, no children. Autocapture still sees clicks there
// (their text is masked and crisis-checked in before_send), unlike
// `ph-no-capture`, which blocks both. Used on Astra's conversation bubbles.
export const REPLAY_PRIVATE_CLASS = 'replay-private';

// The PostHog scripts the page may load, by name (/static/<name>.js) plus the
// project's remote config (/array/<token>/config.js). Everything else
// (logs, surveys, exception autocapture, product tours, conversations,
// tracing headers, site apps, the toolbar) is refused, whatever the project
// settings say.
export const ALLOWED_POSTHOG_SCRIPTS = Object.freeze([
    'posthog-recorder', 'lazy-recorder', 'recorder', 'recorder-v2',
    'web-vitals', 'dead-clicks-autocapture',
    // Not the toolbar: any visitor can be sent a #__posthog= link that loads
    // it, and its own usage metrics bypass before_send (security review,
    // #4475). Heatmaps are viewed inside PostHog instead.
]);

/** posthog-js `prepare_external_dependency_script`: the script tag, or null to
 * refuse it. Fails closed on anything it cannot read. */
export function allowExternalScript(script) {
    try {
        const { pathname } = new URL(script.src);
        if (/^\/array\/[^/]+\/config\.js$/.test(pathname)) return script;
        const m = pathname.match(/^\/static\/(?:[^/]+\/)?([\w-]+)\.js$/);
        return m && ALLOWED_POSTHOG_SCRIPTS.includes(m[1]) ? script : null;
    } catch {
        return null;
    }
}

/** posthog-js `session_recording.maskCapturedNetworkRequestFn`: what replay
 * keeps of a network request (and of the page URL it records). Timing and
 * status only: bodies and headers go even if a setting turned them on, and the
 * URL is cut like every other reported URL. Returning undefined drops it. */
export function maskReplayNetworkRequest(request) {
    try {
        if (!request || typeof request !== 'object') return undefined;
        const out = { ...request };
        for (const k of ['requestBody', 'responseBody', 'requestHeaders', 'responseHeaders']) delete out[k];
        if ('name' in out) out.name = safeUrl(String(out.name ?? ''));
        if ('url' in out) out.url = safeUrl(String(out.url ?? ''));
        return out;
    } catch {
        return undefined;
    }
}

// What the dead-click detector never counts (#5199). It calls a click dead
// when no DOM mutation follows within 2.5 s and no selection change within
// 0.1 s. A native control works without either: a select opens the phone's
// own picker (a pick after 2.5 s changes nothing PostHog can see), and on iOS
// focusing an empty field fires no selection change. The Desk's session read
// for 2026-10-21 reported "dead clicks on the Where do I stand form" in 17 of
// 87 sessions; each dead click on the form was on such a field, and all 17
// visitors went on to run the check. Over 14 days, 137 of 179 dead clicks on a select, input or textarea
// were followed by a change on that same field within 60 s. A label only
// moves focus to its field. Buttons stay counted: a button that does nothing
// is the case this detector exists for. The library's own two defaults are
// kept: a list replaces them (autocapture-utils DEFAULT_DEAD_CLICK_IGNORE_LIST).
export const DEAD_CLICK_IGNORE = Object.freeze([
    '.ph-no-deadclick',
    '.ph-no-capture',
    'select',
    'textarea',
    'label',
    'input:not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="image"])',
]);

// Spread into ph.init(). Each key is read where the note says (posthog-js
// 1.398.7, lib/src/...); a key inside the wrong object is silently ignored,
// which is how #2511 happened, hence the shape test. Built fresh per call
// and never frozen: posthog-js writes into the nested objects (autocapture.js
// _config assigns url_allowlist under "use strict").
export const posthogPrivacyConfig = () => ({
    // Click events: autocapture.js _captureEvent reads both from the top
    // level. Replay shows what was on the page; the events stay text-free so
    // a click inside a blocked conversation bubble carries none of it.
    mask_all_text: true,
    mask_all_element_attributes: true,
    // autocapture.js _config: the copy/cut listener that sends the page
    // selection as $selected_content, independent of mask_all_text.
    autocapture: { capture_copied_text: false },
    // exception-autocapture: false wins over the project. Messages and stack
    // values quote the page and the input.
    capture_exceptions: false,
    // Web vitals ($web_vitals) and replay network timing. Each reported URL is
    // cut by scrubCaptureEvent / maskReplayNetworkRequest.
    capture_performance: { web_vitals: true, network_timing: true },
    // dead-clicks-autocapture: an object turns it on whatever remote config
    // says (isObject, as a boolean true did), and both the $dead_click and the
    // heatmap's detector read its ignore list. A fresh array per call: the
    // library writes __onCapture into this object.
    capture_dead_clicks: { css_selector_ignorelist: [...DEAD_CLICK_IGNORE] },
    // heatmaps.js isEnabled: capture_heatmaps wins over the project setting.
    capture_heatmaps: true,
    // Session replay ON for every visitor (#4475). It starts once the project's
    // remote config says recording is enabled.
    disable_session_recording: false,
    // A client false wins over the project's console-log setting.
    enable_recording_console_log: false,
    session_recording: {
        // Every input, textarea and select is masked (rrweb maskAllInputs).
        maskAllInputs: true,
        // Named, not left to the library default, so an upgrade that renames
        // a default cannot silently unblock a crisis panel or a conversation.
        blockClass: 'ph-no-capture',
        blockSelector: `.${REPLAY_PRIVATE_CLASS}`,
        maskTextClass: 'ph-mask',
        recordBody: false,
        recordHeaders: false,
        captureCanvas: { recordCanvas: false },
        maskCapturedNetworkRequestFn: maskReplayNetworkRequest,
        // Snapshots reach before_send as plain rrweb events, so scrubReplay
        // can cut the URLs inside them. The /s/ request body is still
        // gzip-compressed on the wire (supportedCompression).
        compress_events: false,
    },
    // A survey response is text the student typed. No survey is live; the
    // app never calls the surveys API.
    disable_surveys: true,
    disable_conversations: true,
    disable_product_tours: true,
    opt_in_site_apps: false,
    // Scripts load again (the recorder, web vitals, dead clicks), through an
    // allowlist that refuses the rest.
    disable_external_dependency_loading: false,
    prepare_external_dependency_script: allowExternalScript,
    // Remote config loads (replay needs it); the /flags request does not
    // (its body carries the raw first-visit URL, outside before_send).
    advanced_disable_flags: false,
    advanced_disable_feature_flags: true,
    // Strip fragments inside the library too (href, $current_url, heatmaps,
    // replay URLs).
    disable_capture_url_hashes: true,
    before_send: scrubCaptureEvent,
});
